// Public self-service registration page. Reachable at /register?token=<raw> WITHOUT a
// session (the app root renders it before the auth gate). It validates the invite token on
// mount, then lets the invited person set their OWN email + password. The raw password is
// sent only on the final POST (the server argon2id-hashes it immediately); this form never
// stores a credential. Mirrors the LoginScreen dark IBM-Plex styling.

import { useEffect, useState, type FormEvent } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import type { InviteRole } from '../types/api';
import { C, MONO } from '../lib/tokens';

const MIN_PASSWORD_LEN = 10;

type Phase = 'checking' | 'invalid' | 'ready' | 'submitting' | 'done';

const ROLE_LABEL: Record<InviteRole, string> = {
  viewer: 'Viewer — read-only access',
  operator: 'Operator — read + acknowledge alerts',
  admin: 'Administrator — full management access',
};

/** Read the raw token from the current URL's query string. */
function tokenFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('token') ?? '';
  } catch {
    return '';
  }
}

export function RegisterPage({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<Phase>('checking');
  const [invalidMsg, setInvalidMsg] = useState('This link is invalid or has expired.');
  const [role, setRole] = useState<InviteRole>('viewer');
  const [pinnedEmail, setPinnedEmail] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const token = tokenFromUrl();

  // Validate the token on mount (does not consume it).
  useEffect(() => {
    let alive = true;
    if (!token) {
      setPhase('invalid');
      return;
    }
    api
      .checkInvite(token)
      .then((res) => {
        if (!alive) return;
        setRole(res.role);
        setPinnedEmail(res.email);
        if (res.email) setEmail(res.email);
        setPhase('ready');
      })
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 410) {
          setInvalidMsg('This invitation has expired or was already used.');
        } else {
          setInvalidMsg('This link is invalid or has expired.');
        }
        setPhase('invalid');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  // After a successful registration, hand control back to the login screen.
  useEffect(() => {
    if (phase !== 'done') return;
    const t = setTimeout(onComplete, 2200);
    return () => clearTimeout(t);
  }, [phase, onComplete]);

  const pwTooShort = password.length > 0 && password.length < MIN_PASSWORD_LEN;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    (phase === 'ready') &&
    email.trim().length > 0 &&
    password.length >= MIN_PASSWORD_LEN &&
    confirm === password;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase('submitting');
    setError(null);
    try {
      await api.register({ token, email: email.trim(), password });
      setPhase('done');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not create the account. Please try again.';
      setError(msg);
      setPhase('ready');
    }
  }

  const field: React.CSSProperties = {
    height: 38,
    background: '#0A0F19',
    border: '1px solid ' + C.ln,
    borderRadius: 6,
    color: C.tx,
    fontSize: 13,
    padding: '0 12px',
    width: '100%',
    fontFamily: 'inherit',
  };
  const fieldReadonly: React.CSSProperties = { ...field, color: C.dm, cursor: 'not-allowed' };
  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    color: C.dm,
    fontWeight: 600,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    marginBottom: 5,
    display: 'block',
  };
  const hint: React.CSSProperties = { fontSize: 10.5, marginTop: 4, lineHeight: 1.4 };

  const submitting = phase === 'submitting';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: C.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflow: 'auto',
      }}
    >
      <div
        style={{
          width: 380,
          maxWidth: '94vw',
          background: '#111A2B',
          border: '1px solid ' + C.ln,
          borderRadius: 12,
          padding: '28px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,.5)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.28em', fontWeight: 600 }}>WATCHTOWER</div>
          <div style={{ fontSize: 10.5, color: C.dm, letterSpacing: '.08em' }}>CREATE YOUR ACCOUNT</div>
        </div>

        {phase === 'checking' && (
          <div role="status" style={{ fontSize: 12, color: C.dm, textAlign: 'center', padding: '12px 0', lineHeight: 1.5 }}>
            Validating your invitation…
          </div>
        )}

        {phase === 'invalid' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              role="alert"
              style={{
                fontSize: 12,
                color: C.rd,
                background: 'rgba(228,91,91,.1)',
                border: '1px solid rgba(228,91,91,.35)',
                borderRadius: 6,
                padding: '10px 12px',
                lineHeight: 1.5,
              }}
            >
              {invalidMsg}
            </div>
            <div style={{ fontSize: 11.5, color: C.dm, lineHeight: 1.5 }}>
              Ask an administrator for a new invitation link, then open it again.
            </div>
            <button onClick={onComplete} style={secondaryBtn}>
              Go to sign in
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              role="status"
              style={{
                fontSize: 12.5,
                color: C.gn,
                background: 'rgba(63,191,127,.1)',
                border: '1px solid rgba(63,191,127,.35)',
                borderRadius: 6,
                padding: '10px 12px',
                lineHeight: 1.5,
              }}
            >
              Account created. You can now sign in with your email and password.
            </div>
            <button onClick={onComplete} style={primaryBtn(false)}>
              Continue to sign in
            </button>
          </div>
        )}

        {(phase === 'ready' || phase === 'submitting') && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                fontSize: 11,
                color: C.b9,
                background: 'rgba(76,158,232,.08)',
                border: '1px solid rgba(76,158,232,.3)',
                borderRadius: 6,
                padding: '8px 10px',
                lineHeight: 1.5,
              }}
            >
              You have been invited as <strong style={{ color: C.tx }}>{ROLE_LABEL[role]}</strong>.
            </div>

            <div>
              <label style={labelStyle} htmlFor="wt-reg-email">
                Email
              </label>
              <input
                id="wt-reg-email"
                type="email"
                autoComplete="username"
                value={email}
                readOnly={pinnedEmail != null}
                onChange={(e) => {
                  if (pinnedEmail != null) return;
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                required
                style={pinnedEmail != null ? fieldReadonly : field}
              />
              {pinnedEmail != null && (
                <div style={{ ...hint, color: C.dm }}>This invitation is tied to this email address.</div>
              )}
            </div>

            <div>
              <label style={labelStyle} htmlFor="wt-reg-pw">
                Password
              </label>
              <input
                id="wt-reg-pw"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                required
                style={field}
              />
              <div style={{ ...hint, color: pwTooShort ? C.am : C.dim2 }}>
                {pwTooShort ? `At least ${MIN_PASSWORD_LEN} characters.` : `Use at least ${MIN_PASSWORD_LEN} characters.`}
              </div>
            </div>

            <div>
              <label style={labelStyle} htmlFor="wt-reg-pw2">
                Confirm password
              </label>
              <input
                id="wt-reg-pw2"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  if (error) setError(null);
                }}
                required
                style={field}
              />
              {mismatch && <div style={{ ...hint, color: C.am }}>Passwords do not match.</div>}
            </div>

            {error && (
              <div role="alert" style={{ fontSize: 11.5, color: C.rd, lineHeight: 1.5 }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={!canSubmit || submitting} style={primaryBtn(!canSubmit || submitting)}>
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}

        <div style={{ fontSize: 10, color: C.dim2, textAlign: 'center', lineHeight: 1.5 }}>
          Read-only monitoring console. No trading actions are possible from this interface.
        </div>
      </div>
    </div>
  );
}

function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    height: 40,
    background: C.bl,
    border: 'none',
    borderRadius: 6,
    color: '#0B1220',
    fontWeight: 600,
    fontSize: 13,
    fontFamily: 'inherit',
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'default' : 'pointer',
  };
}

const secondaryBtn: React.CSSProperties = {
  height: 38,
  background: 'none',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  color: C.b9,
  fontWeight: 600,
  fontSize: 12.5,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
